import React, { Children, FC, ReactElement, ReactNode, useEffect, useState } from "react";

interface IEditableCellChildProps {
  onChange?: (...args: unknown[]) => void;
}

interface IEventLikeTarget {
  value?: unknown;
  checked?: unknown;
}

interface IEventLike {
  target?: IEventLikeTarget;
}

interface IKuikaEditableCellProps {
  // String-substituted by TableData. The row's current value for the bound field.
  // Drives display per row, independent of the (shared) form-state key.
  rowValue?: unknown;
  // String-substituted by TableData. Reset trigger when a different row takes
  // this slot (e.g., after sort/pagination/filter).
  rowId?: string | number;
  // Injected by the surrounding antd Form.Item. Ignored for display, used only
  // as a forwarding target so action bindings reading
  // form.getFieldValue('Table_<id>_<field>') still see the picked value.
  value?: unknown;
  onChange?: (val: unknown) => void;
  // Single editable input as child (Combobox, Input, NumberInput, etc.).
  // Typed as ReactNode rather than ReactElement because TableData.setDataFieldValues
  // clones via React.Children.map which can deliver the child wrapped in a
  // length-1 array — the handleChange code resolves both shapes via
  // Children.toArray, with an explicit length-1 assertion to preserve the
  // "exactly one child" contract.
  children: ReactNode;
}

// Per-row display state for an editable Table cell. Form.Item's `name=` binding
// is shared across rows (Table_<id>_<field>), so the form-injected value can't
// be the per-row display source — every row would render the last-edited
// cell's value. KuikaEditableCell instead:
//
//   - displays `internalValue`, seeded from `rowValue` and re-synced when
//     `rowValue` changes (e.g., after a save action refreshes dataSource)
//   - forwards user picks to the form via the injected `onChange`, so
//     action-binding evaluators still read the picked value out of the form
//   - intentionally ignores the form-injected `value` for display
export const KuikaEditableCell: FC<IKuikaEditableCellProps> = ({
  rowValue,
  rowId,
  onChange: formOnChange,
  children
}) => {
  const [internalValue, setInternalValue] = useState<unknown>(rowValue);

  useEffect(() => {
    setInternalValue(rowValue);
  }, [rowValue, rowId]);

  // KOA-17691 — an editable cell is read at save time via its own Form.Item field
  // (DisplayItemProp, e.g. `Switch1.value` / `TextInput1.value`), but KuikaEditableCell writes
  // that field only on a user edit (handleChange). An UNTOUCHED editor therefore commits a
  // stale/shared value — editing only one cell flips the other columns to whatever the last
  // edit left in their shared fields (seen with the boolean Switch, and latent for text). Seed
  // each field from the row's value once on mount so an untouched editor commits the row's
  // current value. Seeding writes the row's own value, so it is correct-by-construction for
  // every editor type. Mount-only: a same-row post-save refresh (rowValue change without a
  // remount) never re-fires this, preserving KOA-13928's "silent refresh" contract; rows are
  // id-keyed so a different row remounts and re-seeds.
  useEffect(() => {
    if (formOnChange) {
      formOnChange(rowValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TableData.setDataFieldValues clones cell content via React.Children.map and
  // passes the resulting array straight into React.cloneElement as the third
  // arg, so any KuikaEditableCell rendered inside a Table cell receives
  // `children` shaped like `[<TheChild/>]` — a length-1 array, not a single
  // element. React.Children.only throws on arrays (isValidElement returns
  // false for arrays). Use Children.toArray, which accepts both shapes, then
  // re-assert the single-child contract explicitly so a violation surfaces
  // with a clearer error than the original Children.only message.
  const childArray: ReactNode[] = Children.toArray(children);
  if (childArray.length !== 1) {
    throw new Error(`KuikaEditableCell expects exactly one child, got ${childArray.length}`);
  }
  const child = childArray[0] as ReactElement;

  // The generated markup wires the user's onChange actions — and, for table
  // renderers on the FindAncestorTableColumn whitelist, a
  // form.setFieldValue('Table_<id>_<field>') write that BaseTable's
  // compareFormValues → handleOnRowEditFinished save flow depends on —
  // directly onto the editable input's own onChange prop. The cloneElement
  // below replaces that prop, so capture it here and chain it from
  // handleChange. Dropping it silences the user's action chain and the
  // row-edit-finished save (KOA-13928 QA round 2, KOA-10142 comment 95233).
  const childOnChange: ((...args: unknown[]) => void) | undefined = (child.props as IEditableCellChildProps).onChange;

  const handleChange = (...args: unknown[]): void => {
    const e: unknown = args[0];
    const eventLike = e as IEventLike | undefined;
    const next: unknown = eventLike?.target?.value ?? eventLike?.target?.checked ?? e;
    setInternalValue(next);
    // Order mirrors rc-field-form's getControlled: the form store is
    // dispatched first, the origin trigger runs second — so the chained
    // handler (and any action it fires) reads the fresh value from the form.
    if (formOnChange) {
      formOnChange(next);
    }
    if (childOnChange) {
      childOnChange(...args);
    }
  };

  return React.cloneElement(child, {
    value: internalValue,
    onChange: handleChange
  });
};
